import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import queryString from 'query-string';
import classNames from 'classnames';
import ReactGA from 'react-ga4';
import camelcaseKeys from 'camelcase-keys';
import {
  ALLOWED_CLIENT_IDS,
  LANDING_PAGE_URL,
  NOTIFICATION_URL,
  VBEE_DOMAIN,
} from '@src/configs';
import {
  COUNTRY,
  EVENT_TYPE,
  SMS_IDENTITY_PROVIDER,
  TIME_DELAY_CALL_API_GET_USER_INFO,
  UPDATE_ONLINE_STATUS_INTERVAL,
  VIETNAM_LOCATION,
} from '@src/constants';
import ROUTES from '@src/constants/route';
import {
  PING_INTERVAL,
  SYNTHESIS_STATUS,
  WS_TYPE,
  WS_ORDER_STATUS,
} from '@src/constants/websocket';
import {
  FREE_PACKAGE_CODES,
  MIGRATED_NAME_PACKAGE_CODES,
  PACKAGE_TYPE,
  TOP_UP_PACKAGE_CODES,
} from '@src/constants/package';
import { BANNER_TYPE } from '@src/constants/banner';
import {
  checkAutoRenewalPackage,
  checkFreePackage,
  checkUsingPrepaidMonthlyPkgOfStudio,
  getPackageName,
} from '@src/services/package';
import { getCookie, removeCookie, setCookie } from '@src/utils/cookie';
import { useDispatch, useSelector } from 'react-redux';
import actions from '@src/redux/actions';
import { STATUS_SURVEY } from '@src/constants/survey';
import ProcessHandler from '@src/components/ProcessHandler';
import Banners from '@src/containers/Banner';
import NotificationDialog from '@src/components/NotificationDialog';
import apis from '@src/apis';
import { DEVICE_STATUS } from '@src/constants/device';
import TransferIcon from '@src/assets/images/transfer-icon.svg';
import { Box, CircularProgress } from '@mui/material';
import NotificationDialogV4 from '@src/components/NotificationDialogV4';
import { AFFILIATE_NOTIFICATION_TYPE } from '@src/constants/affiliate';
import { ORDER_STATUS } from '@src/constants/order';
import moment from 'moment';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import useCustomKeycloak from '@src/hooks/useCustomKeycloak';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { checkShowAutoRenewalPayment } from '@src/services/order';
import { SUBSCRIPTION_PURCHASE_STATUS } from '@src/constants/subscription';
import { checkVietNam } from '@src/utils/checkCountry';
import { delay } from '@src/utils/delay';
import dataSenses from '@src/services/dataSenses';
import { useCheckDubbingRoute } from '@src/hooks/useCheckDubbingRoute';
import useWindowDimensions from '@src/hooks/useWindowDimensions';
import useUserInfo from '@src/hooks/useTtsUser';
import useLocationHook from '@src/hooks/useLocation';
import TitleProduct from '@src/components/TitleProduct';
import LimitedFeaturesDialog from '@src/components/Dialog/LimitedFeaturesDialog';
import {
  getUserUpdateCredits,
  getUserUpdateCreditsProcessWallet,
} from '@src/services/tts';
import useRemoveInjectedIframe from '@src/hooks/useRemoveInjectedIframe';
import i18n from '@src/languages';
import NewSidebar from './SidebarNew/index';
import Content from './Content';
import ServiceTerm from './ServiceTerm';
import { StyledWrapper, StyledMainPanel } from './index.style';
import NewNavbar from './NewNavbar';
import AutoRenewDialog from './AutoRenewal/AutoRenewDialog';
import NewManageDevice from '../NewManageDevice';
import MigratedDubbingDialog from './MigratedDubbingDialog';
import ContentTransferPackage from './ContentTransferPackage';
import Survey from '../Survey';
import { languages } from './SidebarNew/UserInfo';

const Layout = ({ children, headerVoucher, useHeaderBanner }) => {
  useRemoveInjectedIframe();
  const location = useLocation();
  const [ipLocation, seIpLocation] = useState(null);
  const [isExceedDevices, setIsExceedDevices] = useState(false);
  const [showAutoRenewalBanner, setShowAutoRenewalBanner] = useState(false);
  const [userSurvey, setUserSurvey] = useState(false);
  const [banners, setBanners] = useState([]);
  const [hasAgreedToMigrateDubbing, setHasAgreedToMigrateDubbing] =
    useState(true);
  const [hasAgreeToMigrateToV3, setHasAgreeToMigrateToV3] = useState(true);

  const { notificationDialog } = useSelector((state) => state.dialog);
  const { accessToken, user } = useSelector((state) => state.auth);
  const userInfo = useSelector((state) => state.user || {});
  const { synthesisRequest } = useSelector((state) => state.synthesisRequest);
  const { openSidebar } = useSelector((state) => state.layout);
  const { allowShowAutoRenewalBanner } = useSelector((state) => state.banner);
  const {
    isVisible,
    title,
    description,
    onUpgrade,
    onClose,
    img,
    classNameImg,
    maxWidth,
    showButtonUnderstand,
  } = useSelector((state) => state.limitedFeaturesDialog);
  const {
    remainingCharacters,
    bonusCharacters,
    identityProvider,
    preservationExpiryDate,
  } = user;
  const { remainingSeconds } = user?.dubbing || 0;

  const { usingPackage, subscriptionPurchase } = userInfo;
  const { ttsUser } = useUserInfo();
  const { t } = useTranslation();
  const { language } = i18n;
  const ws = useRef(null);
  const remainingCharactersRef = useRef(remainingCharacters);
  const bonusCharactersRef = useRef(bonusCharacters);
  const remainingSecondsRef = useRef(remainingSeconds);
  const userRef = useRef(user);
  const ttsUserRef = useRef(ttsUser);
  const locationRef = useRef(location);
  const languageRef = useRef(language);
  const synthesisRequestRef = useRef(synthesisRequest);
  const translationRef = useRef(t);
  const { keycloak, removeKcTokenCookie } = useCustomKeycloak();
  const isVietNam = checkVietNam();
  const { width: widthScreen } = useWindowDimensions();
  const { ip } = useLocationHook();
  const history = useHistory();
  const dispatch = useDispatch();
  const isSurveyStatusSkippedOrDone = [
    STATUS_SURVEY.SKIPPED,
    STATUS_SURVEY.DONE,
  ].includes(userSurvey?.signup?.status);
  const isDubbingRoute = useCheckDubbingRoute();

  const { getFeatureValue } = useFeatureFlags();

  const useBlockDubbingPayment = getFeatureValue(
    FEATURE_KEYS.BLOCK_DUBBING_PAYMENT,
    { ip },
  );

  const useSyncAffiliateMarketer = getFeatureValue(
    FEATURE_KEYS.SYNC_AFFILIATE_MARKETER,
  );

  const signupSurveyEnable = getFeatureValue(FEATURE_KEYS.SIGNUP_SURVEY, {
    userId: user?.id,
    email: user?.email,
    registeredAt: moment(user?.createdAt).valueOf(),
  });

  const showNewPackagePriceUI = getFeatureValue(
    FEATURE_KEYS.NEW_PRICING_MODEL,
    { ip },
  );

  const useAutoRenewalPayment =
    getFeatureValue(FEATURE_KEYS.CONFIG_AUTO_RENEW_PREPAID_PACKAGE, {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    }) && checkUsingPrepaidMonthlyPkgOfStudio(usingPackage);

  const uiBuyTopupCharacters = getFeatureValue(
    FEATURE_KEYS.UI_BUY_TOP_UP_CHARACTERS,
    { userId: user.id, email: user.email, phoneNumber: user.phoneNumber },
  );

  const isNewHeaderUI = getFeatureValue(FEATURE_KEYS.NEW_HEADER_UI, {
    userId: user.id,
    email: user.email,
    phoneNumber: user.phoneNumber,
  });

  const useShowPopupMigrate = getFeatureValue(
    FEATURE_KEYS.SHOW_POPUP_MIGRATE_V3,
    {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    },
  );

  const isFetchingSignUpSurvey = signupSurveyEnable && userSurvey === false;
  const isDisplaySignUpSurvey =
    signupSurveyEnable && !isSurveyStatusSkippedOrDone;

  const handleChangeBanners = (value) => setBanners(value);

  const handleOpenAutoRenewalPayment = () => {
    const isExpiredPackage =
      !usingPackage.id ||
      (user.packageExpiryDate && moment().isAfter(user.packageExpiryDate));

    const isNotActiveSubPurchase = ![
      SUBSCRIPTION_PURCHASE_STATUS.PENDING,
      SUBSCRIPTION_PURCHASE_STATUS.ACTIVE,
    ].includes(subscriptionPurchase?.status);

    const autoRenewalBanner =
      useAutoRenewalPayment &&
      isVietNam &&
      allowShowAutoRenewalBanner &&
      usingPackage.active &&
      !isExpiredPackage &&
      isNotActiveSubPurchase &&
      checkShowAutoRenewalPayment({
        packageCode: usingPackage?.code,
        alwayShow: false,
      });

    const isSeenAllBanner = allowShowAutoRenewalBanner && !autoRenewalBanner;
    if (isSeenAllBanner) dispatch(actions.banner.addSeenAllBanner());
    setShowAutoRenewalBanner(autoRenewalBanner);
  };

  const handleCloseAutoRenewDialog = (notShowAgain = false) => {
    localStorage.setItem(
      'AUTO_RENEWAL_BANNER',
      JSON.stringify({
        date: moment().format('DD/MM/YYYY'),
        notShowAgain,
      }),
    );
    setShowAutoRenewalBanner(false);
    dispatch(actions.banner.addSeenAllBanner());
  };

  const handleOpenSidebar = () =>
    dispatch(actions.layout.handleChangeOpenSidebar(true));

  const handleCloseSidebar = () =>
    dispatch(actions.layout.handleChangeOpenSidebar(false));

  const handleBackToTTSWhenConfirmedOrder = () => {
    const { pathname, search } = locationRef.current;
    if (pathname === ROUTES.PAYMENT && search.includes('package='))
      dispatch(actions.user.backToTTS());
    if (pathname === ROUTES.PAYMENT_DUBBING && search.includes('package='))
      dispatch(actions.user.backToDubbing());
  };

  const handleCloseQRPaymentWhenCanceledOrder = () => {
    const { pathname, search } = locationRef.current;
    if (
      (pathname === ROUTES.PAYMENT || pathname === ROUTES.PAYMENT_DUBBING) &&
      search.includes('package=')
    )
      dispatch(actions.user.closeQRPayment());
    if (
      pathname === ROUTES.PAYMENT_HISTORY ||
      pathname === ROUTES.PAYMENT_HISTORY_DUBBING
    )
      dispatch(actions.user.fetchPaymentHistory(true));
  };

  const fetchCountry = async () => {
    try {
      const data = await apis.country.getCountry();
      if (!data?.country) {
        seIpLocation(VIETNAM_LOCATION);
        return;
      }
      seIpLocation(data);
    } catch (error) {
      seIpLocation(VIETNAM_LOCATION);
    }
  };

  const isSMSUser = useMemo(
    () => Object.values(SMS_IDENTITY_PROVIDER).includes(identityProvider),
    [identityProvider],
  );

  const showMigrateToV3Popup = useMemo(() => {
    const isExpiredPackage =
      !usingPackage.id ||
      (user.packageExpiryDate && moment().isAfter(user.packageExpiryDate));

    if (isExpiredPackage && user?.lockCharacters === 0) return false;
    if (user?.hasAgreeToMigrateToV3) return false;

    const shouldShowPopup =
      useShowPopupMigrate &&
      preservationExpiryDate &&
      moment().isBefore(preservationExpiryDate) &&
      usingPackage?.code &&
      Object.keys(MIGRATED_NAME_PACKAGE_CODES).includes(usingPackage?.code) &&
      !hasAgreeToMigrateToV3;

    return shouldShowPopup;
  }, [
    user,
    usingPackage,
    hasAgreeToMigrateToV3,
    preservationExpiryDate,
    useShowPopupMigrate,
  ]);

  const fetchSubscriptionPurchase = async (
    purchaseId,
    purchaseStatus,
    isRegisterAutoRenew,
  ) => {
    const data = await apis.subscription.getSubscriptionInfo(purchaseId);
    const { status, result = {} } = data || {};
    if (status === 1) {
      dispatch(actions.user.updateSubscriptionPurchase(result));

      const { orderInfo } = result;
      if (!orderInfo) return;
      const { price, usdPrice, packageExpiryDate, packageCode } = orderInfo;
      const packageName = getPackageName(null, packageCode, language, t, true);

      const hasAutoRenewNotification = getCookie(
        `auto-renewal-notification-${purchaseId}`,
      );
      if (
        !hasAutoRenewNotification &&
        purchaseId &&
        purchaseStatus === SUBSCRIPTION_PURCHASE_STATUS.ACTIVE
      ) {
        if (!isSMSUser && !isRegisterAutoRenew)
          dispatch(
            actions.banner.updateDisplayBanner(
              BANNER_TYPE.JOIN_ZALO_GROUP,
              true,
              {
                packageName,
                isAutoRenewalPackage: true,
                packagePrice: price,
                packageUsdPrice: usdPrice,
                packageExpiryDate,
              },
            ),
          );

        if (!isSMSUser && isRegisterAutoRenew)
          dispatch(
            actions.noti.push({
              severity: 'success',
              message: 'registerAutoRenewSuccess',
            }),
          );

        setCookie({
          cname: `auto-renewal-notification-${purchaseId}`,
          cvalue: true,
          domain: VBEE_DOMAIN,
          extime: 1000 * 60 * 60 * 24 * 14,
        });
      }
    }
  };

  const fetchBannerVoucher = async () => {
    const response = await apis.vouchers.getBannerVouchersFinal(accessToken);

    if (response?.status !== 1) return;
    const bannerVouchers = response?.result?.vouchers || [];
    if (bannerVouchers?.length) {
      dispatch(actions.voucher.updateBannerVoucher(bannerVouchers));
    }
  };

  // const pushDataDeviceToMoe = async () => {
  //   const { result: apiResult = {} } = await apis.devices.getDevices({});
  //   const onlineDevices =
  //     apiResult?.devices?.filter(
  //       (device) => device.status === DEVICE_STATUS.ONLINE,
  //     ).length || 0;
  //   const restrictedDevices =
  //     apiResult?.devices?.filter(
  //       (device) => device.status === DEVICE_STATUS.RESTRICTED,
  //     ).length || 0;

  //   window.dataLayer.push({
  //     event: EVENT_TYPE.DEVICE_MANAGER,
  //     userId: user?.id,
  //     maxDevices: user.maxDevices || userInfo?.usingPackage?.device,
  //     onlineDevices,
  //     restrictedDevices,
  //     time: moment().format('DD/MM/YYYY - HH:mm'),
  //   });
  // };

  const pushNewUserToGTM = async () => {
    const { createdAt, email, id, affiliateMarketerId, pushedToGtm } =
      user || {};
    if (pushedToGtm) return;

    // Only push new user to GTM if user created today
    const checkNewUser = moment().diff(moment(createdAt), 'days') === 0;
    if (!checkNewUser) return;
    dataSenses.sendSignUpSuccessEvent({ userId: id });

    window.dataLayer.push({
      event: EVENT_TYPE.SIGN_UP_SUCCESS,
      email,
      userId: id,
      affiliateMarketerId,
    });

    apis.user.confirmPushUserToGTM();
  };

  const handleSetMoEngageUniqueId = async () => {
    // eslint-disable-next-line no-undef
    await Moengage.add_unique_user_id(user.id);
  };

  const checkUserDeviceV2 = async () => {
    try {
      const data = await apis.devices.checkUserDevice();
      const { check, fingerprint } = data;
      dispatch(
        actions.deviceV2.addDeviceResponse({
          fingerprint,
          status: check,
        }),
      );
      localStorage.setItem('fingerprint-v2', fingerprint);
      // Mobifone user not have email, so we don't check exceed devices
      if (
        (check === DEVICE_STATUS.OFFLINE ||
          check === DEVICE_STATUS.RESTRICTED) &&
        !isSMSUser
      ) {
        setIsExceedDevices(true);
        // pushDataDeviceToMoe();
      } else setIsExceedDevices(false);
    } catch (error) {
      setIsExceedDevices(false);
    }
  };

  const trackingEvents = async () => {
    const { result: apiResult } = await apis.orders.getOrders({
      userId: userRef.current.id,
      status: ORDER_STATUS.PAID,
      sort: 'createdAt_desc',
      limit: 1,
    });
    if (apiResult?.orders?.length === 0) return;
    const order = apiResult?.orders[0] || {};
    const totalPaidOrders = apiResult?.total || 0;
    if (order.pushDataToMoe) return;

    // Send data to GTM
    window.dataLayer.push({
      event: EVENT_TYPE.BUY_PACKAGE,
      customerName: `${user.lastName} ${user.firstName}`,
      email: user.email,
      phoneNumber: user.phoneNumber || order?.user?.phoneNumber,
      orderId: order.id,
      package: order.package?.code,
      type: order.type,
      packageExpiryDate: order.packageExpiryDate,
      packagePrice: order.packagePrice || order?.package?.price,
      paidCharacters:
        order?.customPackage?.maxCharacters || order?.package?.maxCharacters,
      paymentMethodSource:
        order?.paymentMethod?.source || order?.paymentMethodSource,
      confirmedFrom: order?.confirmedFrom,
      confirmedAt: order?.confirmedAt,
      packageType: order?.type,
      totalPaidOrders,
      affiliateMarketerId: order?.affiliateMarketerId,
      userId: user.id,
    });

    apis.orders.updateMoeStatus(order.id);
  };

  const sendOrderToGTM = async ({
    orderId,
    phoneNumber,
    packageCode,
    type,
    packageExpiryDate,
    paidCharacters,
    paymentMethodSource,
    confirmedFrom,
    confirmedAt,
    packageType,
    userId,
  }) => {
    window.dataLayer.push({
      event: EVENT_TYPE.BUY_PACKAGE,
      customerName: `${user.lastName} ${user.firstName}`,
      email: user.email,
      phoneNumber: user.phoneNumber || phoneNumber,
      orderId,
      packageCode,
      type,
      packageExpiryDate,
      paidCharacters,
      paymentMethodSource,
      confirmedFrom,
      confirmedAt,
      packageType,
      userId,
    });

    apis.orders.updateMoeStatus(orderId);
  };

  const handleUpdateAffiliateMarketer = async () => {
    const affiliateCode = getCookie('affiliateCode');
    const affiliateTime = getCookie('affiliateTime');
    if (!affiliateCode || !affiliateTime) return;

    await apis.affiliateMarketer.updateAffiliateMarketer({
      affiliateCode,
      affiliateTime,
      accessToken,
    });

    // Remove cookies after update
    removeCookie('affiliateCode');
    removeCookie('affiliateTime');
  };

  const checkUserAffiliateMatch = async () => {
    const affiliateCode = getCookie('affiliateCode');
    const affiliateTime = getCookie('affiliateTime');
    if (!affiliateCode || !affiliateTime) return;

    const res = await apis.affiliateMarketer.checkUserAffiliateMatch({
      affiliateCode,
      affiliateTime,
      accessToken,
    });

    // Remove cookies when affiliate match
    if (res?.result?.isMatched) {
      removeCookie('affiliateCode');
      removeCookie('affiliateTime');
    }
  };

  const handleUpdateAdsProvider = async () => {
    const adsProvider = getCookie('adsProvider');
    const adsProviderTime = getCookie('adsProviderTime');
    if (!adsProvider || !adsProviderTime) return;

    await apis.adsProvider.updateAdsProvider({
      adsProvider,
      adsProviderTime,
    });

    // Remove cookies after update
    removeCookie('adsProvider');
    removeCookie('adsProviderTime');
  };

  const handleShowNotiWhenCancelSubscription = async () => {
    const { search } = locationRef.current;
    const params = new URLSearchParams(search);
    const purchaseId = params.get('purchaseId');
    const status = params.get('status');

    if (purchaseId && status.includes(ORDER_STATUS.CANCEL)) {
      // Delay 2s for waiting display ui
      await delay(2000);

      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'transactionFailed',
        }),
      );

      // Remove query params after display notification
      history.replace({
        pathname: location.pathname,
        search: '',
      });
    }
  };

  const handleUpdateUserSurvey = (status) =>
    setUserSurvey({
      ...userSurvey,
      signup: { ...userSurvey?.signup, status },
    });

  const handleChangeLanguage = (lng) => {
    i18n.changeLanguage(lng.value);
  };

  useEffect(() => {
    const currentLanguageValue = getCookie('lng');
    const currentLanguage = languages.find(
      (lng) => lng.value === currentLanguageValue,
    );

    if (ipLocation) {
      const { country: countryCode } = ipLocation;
      localStorage.setItem('country', COUNTRY[countryCode] || COUNTRY.EN);
    }

    if (currentLanguage) {
      handleChangeLanguage(currentLanguage);
      return;
    }

    const countryLanguageValue = localStorage.getItem('country');
    const countryLanguage = languages.find(
      (lng) => lng.country === countryLanguageValue,
    );
    if (countryLanguage) {
      handleChangeLanguage(countryLanguage);
      return;
    }

    handleChangeLanguage(languages[0]);
  }, [ipLocation]);

  useEffect(() => {
    const getUserSurvey = async () => {
      const response = await apis.survey.getSurveyUser();
      setUserSurvey(response?.result);
    };
    getUserSurvey();
  }, []);

  // Default open sidebar
  // Close sidebar when screen width < 600px
  useEffect(() => {
    if (widthScreen < 600) {
      handleCloseSidebar();
    }
  }, [widthScreen]);

  // Push login event to datasenses
  useEffect(() => {
    dataSenses.sendLoginEvent({ userId: user.id });
  }, [keycloak.authenticated]);

  useEffect(() => {
    ReactGA.set({ userId: user.id });
  }, [keycloak.authenticated]);

  useEffect(() => {
    handleOpenAutoRenewalPayment();
  }, [usingPackage, allowShowAutoRenewalBanner, subscriptionPurchase]);

  useEffect(() => {
    if (useSyncAffiliateMarketer) checkUserAffiliateMatch();
    else handleUpdateAffiliateMarketer();

    handleUpdateAdsProvider();
  }, []);

  useEffect(() => {
    pushNewUserToGTM();
    handleSetMoEngageUniqueId();
  }, [user]);

  useEffect(() => {
    remainingCharactersRef.current = remainingCharacters;
  }, [remainingCharacters]);

  useEffect(() => {
    bonusCharactersRef.current = bonusCharacters;
  }, [bonusCharacters]);

  useEffect(() => {
    remainingSecondsRef.current = remainingSeconds;
  }, [remainingSeconds]);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    ttsUserRef.current = ttsUser;
  }, [ttsUser]);

  useEffect(() => {
    locationRef.current = location;
    handleShowNotiWhenCancelSubscription();
  }, [location]);

  useEffect(() => {
    languageRef.current = language;
  }, [language]);

  useEffect(() => {
    synthesisRequestRef.current = synthesisRequest;
  }, [synthesisRequest]);

  useEffect(() => {
    translationRef.current = t;
  }, [t]);

  useEffect(() => {
    apis.account.updateOnlineStatus();

    const updateOnlineStatusInterval = setInterval(() => {
      apis.account.updateOnlineStatus();
    }, UPDATE_ONLINE_STATUS_INTERVAL);

    return () => clearInterval(updateOnlineStatusInterval);
  }, []);

  useEffect(() => {
    checkUserDeviceV2();
  }, [location?.pathname]);

  useEffect(() => {
    fetchSubscriptionPurchase();
    if (!isSMSUser) {
      fetchBannerVoucher();
    }
    dispatch(actions.user.getTtsUser());
  }, []);

  useEffect(() => {
    const createNotificationWSConnection = async () => {
      ws.current = new WebSocket(NOTIFICATION_URL);
      let pingInterval;
      ws.current.onopen = () => {
        ws.current.send(JSON.stringify({ type: WS_TYPE.INIT, accessToken }));
        pingInterval = setInterval(() => {
          ws.current.send(JSON.stringify({ type: WS_TYPE.PING }));
        }, PING_INTERVAL);
      };

      ws.current.onmessage = (res) => {
        const responseData = camelcaseKeys(JSON.parse(res.data), {
          deep: true,
        });
        const { type, result } = responseData;

        const handleSynthesis = () => {
          const {
            requestId,
            audioLink,
            status,
            characters,
            seconds,
            progress,
            processingAt,
            endedAt,
            blockedCredits,
            sentencesCreditsInfo,
          } = result;

          switch (status) {
            case SYNTHESIS_STATUS.CREATED: {
              if (seconds) {
                let currentSeconds = remainingSecondsRef.current;
                currentSeconds -= seconds;
                dispatch(
                  actions.auth.updateUserInfo({
                    ...userRef.current,
                    dubbing: {
                      ...userRef.current.dubbing,
                      remainingSeconds: currentSeconds,
                    },
                  }),
                );
              } else {
                const updatedUser = userRef.current;
                const updatedTtsUser = ttsUserRef.current;

                // order of subtract credit: topup -> cycle -> onetime (bonusCharacters -> remainingChracters)
                const userUpdatedCredits = sentencesCreditsInfo
                  ? getUserUpdateCreditsProcessWallet({
                      sentencesCreditsInfo,
                      currWallets: {
                        onetimeCredits:
                          updatedUser.remainingCharacters +
                          updatedUser.bonusCharacters,
                        cycleCredits:
                          updatedTtsUser?.studio?.cycle?.remainingCredits || 0,
                        topUpCredits:
                          updatedTtsUser?.studio?.topUp?.remainingCredits || 0,
                        customCredits:
                          updatedTtsUser?.studio?.custom?.remainingCredits || 0,
                      },
                      isFreeUser: checkFreePackage(user.packageCode),
                    })
                  : getUserUpdateCredits({
                      ttsUser: updatedTtsUser,
                      user: updatedUser,
                      credits: characters,
                      blockedCredits,
                    });

                updatedUser.remainingCharacters =
                  userUpdatedCredits.remainingCharacters || 0;
                updatedUser.bonusCharacters =
                  userUpdatedCredits.bonusCharacters || 0;

                dispatch(
                  actions.auth.updateUserInfo({
                    ...updatedUser,
                  }),
                );

                dispatch(
                  actions.user.getTtsUserSuccess({
                    ...updatedTtsUser,
                    studio: {
                      ...(updatedTtsUser?.studio || {}),
                      cycle: {
                        ...(updatedTtsUser?.studio?.cycle || {}),
                        remainingCredits:
                          userUpdatedCredits['studio.cycle.remainingCredits'] ||
                          0,
                      },
                      topUp: {
                        ...(updatedTtsUser?.studio?.topUp || {}),
                        remainingCredits:
                          userUpdatedCredits['studio.topUp.remainingCredits'] ||
                          0,
                      },
                      custom: {
                        ...(updatedTtsUser?.studio?.custom || {}),
                        remainingCredits:
                          userUpdatedCredits[
                            'studio.custom.remainingCredits'
                          ] || 0,
                      },
                    },
                  }),
                );
              }

              break;
            }
            case SYNTHESIS_STATUS.SUCCESS: {
              if (!processingAt || !endedAt) break; // To avoid update synthesisRequest when backend send message with status SUCCESS but not send processingAt and endedAt
              dispatch(
                actions.request.addSynthesisResponse({
                  requestId,
                  audioLink,
                  status,
                  processingAt,
                  endedAt,
                }),
              );
              break;
            }

            case SYNTHESIS_STATUS.FAILURE: {
              const paid = result.paidCharacters || 0;
              const bonus = result.bonusCharacters || 0;
              dispatch(
                actions.request.addSynthesisResponse({
                  requestId,
                  audioLink,
                  status,
                }),
              );
              dispatch(
                actions.auth.updateUserInfo({
                  ...userRef.current,
                  remainingCharacters: remainingCharactersRef.current + paid,
                  bonusCharacters: bonusCharactersRef.current + bonus,
                }),
              );
              break;
            }
            case SYNTHESIS_STATUS.IN_PROGRESS: {
              const updateData = { requestId, audioLink, status, progress };
              if (processingAt) updateData.processingAt = processingAt;
              dispatch(actions.request.addSynthesisResponse(updateData));

              const startProcessSynthesisRequest =
                synthesisRequestRef.current.id === requestId && progress === 1; // 1 is 1% - start percent when request start processing
              if (startProcessSynthesisRequest) {
                if (processingAt)
                  synthesisRequestRef.current.processingAt = processingAt;
                dispatch(
                  actions.synthesisRequest.updateSynthesisConfig(
                    synthesisRequestRef.current,
                  ),
                );
              }
              break;
            }
            default:
              break;
          }

          if (synthesisRequestRef.current.id === requestId) {
            if (
              status === SYNTHESIS_STATUS.SUCCESS &&
              (!processingAt || !endedAt)
            )
              return; // return to avoid update synthesisRequest when backend send message with status SUCCESS but not send processingAt and endedAt

            dispatch(
              actions.synthesisRequest.updateSynthesisConfig({
                ...synthesisRequestRef.current,
                progress,
                status,
                audioLink,
              }),
            );
          }
        };

        const updateUserInfo = async (isDelay) => {
          // Delay time for wait update database reset bonus characters
          if (isDelay) await delay(TIME_DELAY_CALL_API_GET_USER_INFO);
          dispatch(actions.user.getTtsUser());
          const userRes = await apis.account.getMe();
          dispatch(actions.auth.updateUserInfo(userRes.result));
        };

        const handleOrder = async () => {
          const {
            type: packageType,
            packageCode,
            renewalType,
            packagePrice,
            packageUsdPrice,
            packageExpiryDate,
            status,
            remainingCharacters: remaining,
            bonusCharacters: bonus,
            lockCharacters,
            remainingSeconds: resultSeconds,
            lockSeconds,
            isGift,
            studio,
          } = result;
          const isAutoRenewalPackage = checkAutoRenewalPackage({ renewalType });
          switch (status) {
            case WS_ORDER_STATUS.PAID: {
              if (packageType === PACKAGE_TYPE.API) {
                handleBackToTTSWhenConfirmedOrder();
                break;
              }

              if (packageCode) {
                const isFreePackage = FREE_PACKAGE_CODES.includes(packageCode);

                if (!isFreePackage) {
                  const packageName = getPackageName(
                    null,
                    packageCode,
                    languageRef.current,
                    translationRef.current,
                    true,
                  );
                  if (!isGift || !isSMSUser) {
                    dispatch(
                      actions.banner.updateDisplayBanner(
                        BANNER_TYPE.JOIN_ZALO_GROUP,
                        true,
                        {
                          isTopUpPackage:
                            TOP_UP_PACKAGE_CODES.includes(packageCode) &&
                            uiBuyTopupCharacters,
                          packageType,
                          packageName,
                          isAutoRenewalPackage,
                          packagePrice,
                          packageUsdPrice,
                          packageExpiryDate,
                        },
                      ),
                    );

                    dispatch(
                      actions.noti.push({
                        severity: 'success',
                        message: 'buyPackageSuccessfully',
                      }),
                    );
                  }
                }

                const updatedUserInfo = { ...userRef.current };
                if (packageType === PACKAGE_TYPE.DUBBING) {
                  updatedUserInfo.dubbing = {
                    ...userRef.current.dubbing,
                    packageCode,
                    packageExpiryDate,
                  };
                  dispatch(
                    actions.user.getLatestDubbingOrder(userRef.current.id),
                  );
                } else {
                  if (!TOP_UP_PACKAGE_CODES.includes(packageCode)) {
                    updatedUserInfo.packageCode = packageCode;
                  }

                  updatedUserInfo.packageExpiryDate = packageExpiryDate;
                  dispatch(actions.user.getLatestOrder(userRef.current.id));
                }
                dispatch(actions.auth.updateUserInfo(updatedUserInfo));
                dispatch(actions.user.getUsingPackage(userRef.current.id));
                dispatch(actions.order.updateVoucher(null));
                dispatch(actions.user.getTtsUser());
                handleBackToTTSWhenConfirmedOrder();

                trackingEvents();
              }

              if (resultSeconds || lockSeconds) {
                dispatch(
                  actions.auth.updateUserInfo({
                    ...userRef.current,
                    dubbing: {
                      ...userRef.current.dubbing,
                      remainingSeconds: resultSeconds,
                      lockSeconds,
                    },
                  }),
                );
              }

              if (remaining || bonus || lockCharacters) {
                dispatch(
                  actions.auth.updateUserInfo({
                    ...userRef.current,
                    remainingCharacters: remaining,
                    bonusCharacters: bonus,
                    lockCharacters,
                  }),
                );
              }

              if (
                studio &&
                (studio?.cycle?.remainingCredits ||
                  studio?.topUp?.remainingCredits)
              ) {
                dispatch(
                  actions.auth.updateUserInfo({
                    ...userRef.current,
                    bonusCharacters: 0,
                  }),
                );
                const updatedCreditTtsUser = { ...ttsUserRef.current };
                dispatch(
                  actions.user.getTtsUserSuccess({
                    ...updatedCreditTtsUser,
                    studio: {
                      ...(updatedCreditTtsUser?.studio || {}),
                      cycle: {
                        ...(updatedCreditTtsUser?.studio?.cycle || {}),
                        remainingCredits: studio?.cycle?.remainingCredits,
                      },
                      topUp: {
                        ...(updatedCreditTtsUser?.topUp || {}),
                        remainingCredits: studio?.topUp?.remainingCredits,
                      },
                    },
                  }),
                );
              }

              if (
                studio &&
                studio?.cycle?.nextResetAt &&
                studio?.cycle?.period
              ) {
                const updatedTtsUser = { ...ttsUserRef.current };
                dispatch(
                  actions.user.getTtsUserSuccess({
                    ...updatedTtsUser,
                    studio: {
                      ...(updatedTtsUser?.studio || {}),
                      cycle: {
                        ...(updatedTtsUser?.studio?.cycle || {}),
                        nextResetAt: studio.cycle.nextResetAt,
                        period: studio.cycle.period,
                      },
                    },
                  }),
                );
              }

              await updateUserInfo();
              break;
            }

            case WS_ORDER_STATUS.CANCEL: {
              if (packageType === PACKAGE_TYPE.DUBBING) {
                dispatch(
                  actions.user.getLatestDubbingOrder(userRef.current.id),
                );
                if (isDubbingRoute) {
                  dispatch(
                    actions.noti.push({
                      severity: 'success',
                      message: 'notiCanceledOrder',
                    }),
                  );
                  handleCloseQRPaymentWhenCanceledOrder();
                }
              } else {
                dispatch(actions.user.getLatestOrder(userRef.current.id));
                if (!isDubbingRoute) {
                  dispatch(
                    actions.noti.push({
                      severity: 'success',
                      message: 'notiCanceledOrder',
                    }),
                  );
                  handleCloseQRPaymentWhenCanceledOrder();
                }
              }

              dispatch(actions.order.updateVoucher(null));
              dispatch(actions.user.lockCycleTopUpCredits(false));
              break;
            }

            default:
              break;
          }
        };

        const handleLogout = (fingerprintDevice) => {
          const fingerprint = localStorage.getItem('fingerprint-v2') || '';

          const isMatchingDevice = fingerprintDevice === fingerprint;

          if (isMatchingDevice) {
            removeKcTokenCookie();
            keycloak.logout({ redirectUri: LANDING_PAGE_URL, fingerprint });
          }
        };

        const handleAffiliateNotification = () => {
          const { notificationType } = result;
          switch (notificationType) {
            case AFFILIATE_NOTIFICATION_TYPE.CREATE_AFFILIATE_TRANSACTION_FAILURE: {
              dispatch(
                actions.noti.push({
                  severity: 'error',
                  message: 'createAffiliateTransactionFailure',
                }),
              );
              break;
            }

            // case AFFILIATE_NOTIFICATION_TYPE.CREATE_AFFILIATE_TRANSACTION_SUCCESS: {
            //   dispatch(
            //     actions.noti.push({
            //       severity: 'success',
            //       message: 'createAffiliateTransactionSuccess',
            //     }),
            //   );
            //   break;
            // }

            case AFFILIATE_NOTIFICATION_TYPE.AFFILIATE_TRANSACTION_APPROVED: {
              dispatch(
                actions.noti.push({
                  severity: 'success',
                  message: 'paymentRequestApproved',
                }),
              );
              break;
            }

            case AFFILIATE_NOTIFICATION_TYPE.AFFILIATE_TRANSACTION_REJECTED: {
              dispatch(
                actions.noti.push({
                  severity: 'error',
                  message: 'paymentRequestRejected',
                }),
              );
              break;
            }

            default: {
              break;
            }
          }
        };

        const syncAccountLogout = ({ clientId, fingerprint, redirectUri }) => {
          const fingerprintClient =
            localStorage.getItem('fingerprint-v2') || '';

          const isMatchingDevice = fingerprintClient === fingerprint;
          const isMatchingClient = clientId
            ? ALLOWED_CLIENT_IDS.includes(clientId)
            : true;

          if (isMatchingDevice && isMatchingClient) {
            window.location.href = redirectUri || LANDING_PAGE_URL;
          }
        };

        switch (type) {
          case WS_TYPE.SYNTHESIS: {
            handleSynthesis();
            break;
          }

          case WS_TYPE.INIT: {
            updateUserInfo(true);
            break;
          }

          case WS_TYPE.ORDER: {
            handleOrder();
            break;
          }

          case WS_TYPE.LOGOUT_DEVICES: {
            const { fingerprint } = result;
            dispatch(
              actions.device.addDeviceResponse({
                fingerprint,
                status: DEVICE_STATUS.OFFLINE,
              }),
            );
            handleLogout(fingerprint);
            break;
          }

          case WS_TYPE.AFFILIATE_NOTIFICATION: {
            handleAffiliateNotification();
            break;
          }

          case WS_TYPE.RESET_FREE_CHARACTERS: {
            dispatch(
              actions.auth.updateBonusCharacters(result.bonusCharacters),
            );
            dispatch(
              actions.user.updateBonusCharacters(result.bonusCharacters),
            );
            break;
          }

          case WS_TYPE.PUSH_ORDER_TO_GTM: {
            sendOrderToGTM(result);
            break;
          }

          case WS_TYPE.SYNC_ACCOUNT_LOGOUT: {
            const { clientId, fingerprint, redirectUri } = result;
            syncAccountLogout({ clientId, fingerprint, redirectUri });
            break;
          }

          default:
            break;
        }
      };

      ws.current.onclose = (event) => {
        const { code, reason, wasClean } = event;
        // eslint-disable-next-line no-console
        console.log('Websocket is closed.', { event, code, reason, wasClean });
        clearInterval(pingInterval);
        createNotificationWSConnection();
      };
    };

    // Initialize WebSocket connection
    createNotificationWSConnection();

    return () => {
      // Clean up on component unmount
      ws.current?.close();
    };
  }, []);

  useEffect(() => {
    fetchCountry();
  }, []);

  useEffect(() => {
    if (!banners.length) return;
    // Handle receive subscription redirect result
    const { purchaseId, status, registerAutoRenew } = camelcaseKeys(
      queryString.parse(location.search) || {},
    );
    if (purchaseId) {
      fetchSubscriptionPurchase(purchaseId, status, registerAutoRenew);
    }
  }, [location, banners]);

  useEffect(() => {
    // Add trial dubbing for user when user access to dubbing page and user has not dubbing package
    if (
      !user ||
      user?.dubbing?.packageCode ||
      !location.pathname.includes('/dubbing') ||
      useBlockDubbingPayment
    )
      return;
    apis.user.addTrialDubbing();
  }, [user, location.pathname]);

  // useEffect(() => {
  //   dataSenses.sendViewPageEvent({
  //     userId: user.id,
  //     domain: window.location.hostname,
  //     pathname: location.pathname,
  //     search: location.search,
  //   });
  // }, [location.pathname, location.search]);

  const isBackgroundWhiteMobile = [ROUTES.VOICE_CLONING_VOICES].includes(
    location.pathname,
  );

  useEffect(() => {
    if (
      ttsUser &&
      ttsUser?.migratedDubbing &&
      !ttsUser?.migratedDubbing?.hasAgreedToMigrate
    )
      setHasAgreedToMigrateDubbing(false);

    if (ttsUser && ttsUser?.id) {
      const hasAgreedToMigrateOldPkgToV3 = localStorage.getItem(
        `hasAgreeToMigrateToV3-${ttsUser?.id}`,
      );
      if (!hasAgreedToMigrateOldPkgToV3) {
        setHasAgreeToMigrateToV3(false);
      }
    }
  }, [ttsUser]);

  const renderContent = () => {
    if (!ipLocation) return <ProcessHandler loading={!location} />;
    if (
      ipLocation &&
      isExceedDevices &&
      location?.pathname !== ROUTES.MANAGE_DEVICE
    )
      return (
        <>
          <StyledWrapper>
            <NewSidebar
              openSidebar={openSidebar}
              handleOpenSidebar={handleOpenSidebar}
              handleCloseSidebar={handleCloseSidebar}
              ipLocation={ipLocation}
            />

            <StyledMainPanel
              className={classNames({ 'open-sidebar': openSidebar })}
            >
              <NewNavbar
                openSidebar={openSidebar}
                handleOpenSidebar={handleOpenSidebar}
                ipLocation={ipLocation}
              />

              <Content openSidebar={openSidebar}>
                <NotificationDialog
                  name="exceed-devices"
                  width="566px"
                  hiddenIcon
                  dialogDevice
                  title={t('accountDeviceExceedTitle')}
                  contentComponent={
                    <NewManageDevice
                      isPopup
                      dialogDevice
                      isExceedDevices={isExceedDevices}
                      checkUserDevice={checkUserDeviceV2}
                    />
                  }
                  open={isExceedDevices}
                />
              </Content>
            </StyledMainPanel>
          </StyledWrapper>
        </>
      );

    if (isFetchingSignUpSurvey) {
      return (
        <Box
          height="100vh"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <CircularProgress color="primary" />
        </Box>
      );
    }

    if (isDisplaySignUpSurvey) {
      return (
        <NotificationDialog
          hiddenIcon
          contentComponent={
            <Survey
              closeUserSurvey={handleUpdateUserSurvey}
              steps={userSurvey?.signup?.steps}
            />
          }
          open
        />
      );
    }

    return (
      <>
        <StyledWrapper
          useHeaderBanner={useHeaderBanner}
          headerVoucher={headerVoucher}
        >
          <NewSidebar
            useHeaderBanner={useHeaderBanner}
            headerVoucher={headerVoucher}
            openSidebar={openSidebar}
            handleOpenSidebar={handleOpenSidebar}
            handleCloseSidebar={handleCloseSidebar}
            ipLocation={ipLocation}
          />

          <StyledMainPanel
            openSidebar={openSidebar}
            isBackgroundWhiteMobile={isBackgroundWhiteMobile}
            className="main-panel"
          >
            <NewNavbar
              openSidebar={openSidebar}
              handleOpenSidebar={handleOpenSidebar}
              ipLocation={ipLocation}
            />

            <Content
              useHeaderBanner={useHeaderBanner}
              headerVoucher={headerVoucher}
              showNewPackagePriceUI={showNewPackagePriceUI}
              openSidebar={openSidebar}
            >
              <TitleProduct
                isSMSUser={isSMSUser}
                isNewHeaderUI={isNewHeaderUI}
              />
              {children}
            </Content>
          </StyledMainPanel>
          {!isSMSUser && (
            <Banners banners={banners} onChangeBanners={handleChangeBanners} />
          )}
          {notificationDialog?.open && (
            <NotificationDialog
              name={notificationDialog.name}
              title={notificationDialog.title}
              description={notificationDialog.description}
              subDescription={notificationDialog.subDescription}
              variant={notificationDialog.variant}
              open={notificationDialog.open}
              width={notificationDialog.width}
              contentComponent={notificationDialog.contentComponent}
              actionComponent={notificationDialog.actionComponent}
              onClose={notificationDialog.onClose}
            />
          )}
          {showMigrateToV3Popup && (
            <NotificationDialogV4
              isPopupTransfer
              image={TransferIcon}
              hideImageOnMobileWeb
              title={t('announcementUpperCase')}
              contentPopup={
                <ContentTransferPackage
                  setHasAgreeToMigrateToV3={setHasAgreeToMigrateToV3}
                />
              }
              open={!hasAgreeToMigrateToV3}
              showIconClose={false}
            />
          )}
          <ServiceTerm open={!user?.hasAgreedToTerms} />
          <AutoRenewDialog
            open={showAutoRenewalBanner}
            onClose={handleCloseAutoRenewDialog}
          />
        </StyledWrapper>
        {isVisible && (
          <LimitedFeaturesDialog
            open={isVisible}
            title={title}
            description={description}
            onUpgrade={onUpgrade}
            onClose={onClose}
            img={img}
            classNameImg={classNameImg}
            maxWidth={maxWidth}
            showButtonUnderstand={showButtonUnderstand}
          />
        )}
        {!hasAgreedToMigrateDubbing && (
          <MigratedDubbingDialog
            open={!hasAgreedToMigrateDubbing}
            onClose={() => setHasAgreedToMigrateDubbing(true)}
          />
        )}
      </>
    );
  };

  return renderContent();
};

export default Layout;
